import $ from 'jquery';
import {
  LogsDictionary,
  LogsDetailsDictionary,
  initStateObj,
  samplesMinPlayerHeight,
} from '../constants/Constants';
import {
  isMobile,
  players,
  samplePlayers,
  playlist,
  playlistJW5,
  isIPhone,
  isIPad,
} from '../constants/Constants';

/**
 * Method to check if we already collect log about some error
 * The purpose of it is to avoid double log about the same error,
 * From controller we can recives 2 logs: parserLogError and generalError, about the same issue
 * We still need to listen for both kind of logs, becuase for some cases only one generalError would be send
 * @param {object} collection the collection of already reported logs
 * @param {object} log current log comming from controller
 * @param {object} log.type the log type
 * @param {array} errorKeys an Array with errors keys
 * @returns {boolean} true if we can add comming error log, false if some error already was reported
 */
export const checkUniqueError = (collection, log, errorKeys) => {
  let errorNumber = 0;

  for (const key of errorKeys) {
    const collectionHasKey = collection.some(logItem => logItem.type === key);
    const logHasKey = errorKeys.includes(log.type);

    if (collectionHasKey && logHasKey) {
      errorNumber++;
    }
  }

  return errorNumber === 0;
};

/**
 * Method returns search query parameters
 * @param {String} search query of the url
 * @returns {Object} search query parameters, name + value
 */
export const decodeURLParams = search => {
  const hashes = search.slice(search.indexOf('?') + 1).split('&');
  return hashes.reduce((params, hash) => {
    const split = hash.indexOf('=');
    const key = hash.slice(0, split);
    const val = hash.slice(split + 1);
    return Object.assign(params, { [key]: decodeURIComponent(val) });
  }, {});
};

/**
 * Method returns the localStorage value
 * @param {String} name the name of the cookie variable
 * @returns {String} the cookie variable's value
 */
export const getLocalStorage = name => {
  return globalThis.localStorage.getItem(name);
};

/**
 * Method return current data in format : "HH:MM:SS"
 * @returns {string}
 */
export const getCurrentDate = () => {
  let data = new Date(),
    h = data.getHours(),
    m = data.getMinutes(),
    s = data.getSeconds();
  return (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
};

/**
 * Method to transform log from controller to showcase readable information.
 * This method also select which logs we display on showcase (for example log: 'clear' is omitted)
 * @param {object} log the log from controller
 * @param {object} log.type the log type
 * @param {object} log.data the log data
 * @param {string} log.data.logDetails the log details
 * @param {number|null} log.data.inventory number of parsed ads
 * @returns {string|null} readable log or null if log type not found in showcase dictionary
 */
export const getVastParserLog = log => {
  let result = null;

  if (LogsDictionary[log.type] !== undefined) {
    result =
      log.type === 'parserLogError' || log.type === 'parserLog' || log.type === 'generalError'
        ? log.data.logDetails !== undefined
          ? LogsDictionary[log.type] + LogsDetailsDictionary[log.data.logDetails]
          : log.data.errorType !== undefined
          ? LogsDictionary[log.type] + LogsDetailsDictionary[log.data.errorType]
          : null
        : LogsDictionary[log.type];
  }

  if (log.type === 'AdError' && log.data.code !== 'AdError') {
    result = LogsDictionary[log.type] + LogsDetailsDictionary[log.data.code];
  }

  if (log.data.inventory !== undefined) {
    result += ' Number of parsed ads : ' + log.data.inventory;
  }
  if (
    log.type === 'impressionLogged' &&
    log.data.errorCount !== undefined &&
    log.data.successCount !== undefined &&
    log.data.errorCount > 0
  ) {
    if (log.data.successCount === 0) {
      result = 'No impression was successfully logged';
    } else {
      result =
        log.data.successCount +
        ' out of ' +
        (log.data.errorCount + log.data.successCount) +
        ' impressions' +
        (log.data.successCount > 1 ? ' were' : ' was') +
        ' successfully logged';
    }
  }
  return result;
};

/**
 * Try to parse and deocde JSON to object
 * Imported settings are merged with init Object to fulfill missing fields
 * @param {String} external settings
 * @returns {Object|error} new Object with valid settings, or error if input param can not be parse
 */
export const importSettings = data => {
  let result;

  try {
    let importedData;

    try {
      importedData = JSON.parse(data);
    } catch (error) {
      // eslint-disable-next-line
      importedData = eval('(' + data + ')');
    }

    if (typeof importedData !== 'object') {
      throw new Error();
    }

    result = Object.assign({}, initStateObj, {
      adRules: {
        isValid: initStateObj.adRules.isValid,
        data: importedData.adRules
          ? mergeAdRules(
              JSON.parse(JSON.stringify(importedData.adRules)),
              initStateObj.adRules.data[0]
            )
          : initStateObj.adRules.data,
      },
      adData: importedData.adData
        ? mergeObjects(initStateObj.adData, importedData.adData)
        : initStateObj.adData,
      configuration: {
        isValid: initStateObj.configuration.isValid,
        labels:
          importedData.configuration && importedData.configuration.labels
            ? mergeObjects(initStateObj.configuration.labels, importedData.configuration.labels)
            : initStateObj.configuration.labels,
        overlays:
          importedData.configuration && importedData.configuration.overlays
            ? mergeObjects(initStateObj.configuration.overlays, importedData.configuration.overlays)
            : initStateObj.configuration.overlays,
        companions:
          importedData.configuration && importedData.configuration.companions
            ? mergeObjects(
                initStateObj.configuration.companions,
                importedData.configuration.companions
              )
            : { ...initStateObj.configuration.companions, slot: '' },
        banner:
          importedData.configuration && importedData.configuration.banner
            ? mergeObjects(initStateObj.configuration.banner, importedData.configuration.banner)
            : initStateObj.configuration.banner,
        player:
          importedData.configuration && importedData.configuration.player
            ? mergeObjects(initStateObj.configuration.player, importedData.configuration.player)
            : initStateObj.configuration.player,
        publisher:
          importedData.configuration && importedData.configuration.publisher
            ? mergeObjects(
                initStateObj.configuration.publisher,
                importedData.configuration.publisher
              )
            : initStateObj.configuration.publisher,
        rtb:
          importedData.configuration && importedData.configuration.rtb
            ? mergeObjects(initStateObj.configuration.rtb, importedData.configuration.rtb)
            : initStateObj.configuration.rtb,
        vpaid:
          importedData.configuration && importedData.configuration.vpaid
            ? mergeObjects(initStateObj.configuration.vpaid, importedData.configuration.vpaid)
            : initStateObj.configuration.vpaid,
      },
      other: {
        isValid: initStateObj.other.isValid,
        user: importedData.user
          ? mergeObjects(initStateObj.other.user, importedData.user)
          : initStateObj.other.user,
        content: importedData.contentData
          ? mergeObjects(initStateObj.other.content, importedData.contentData)
          : initStateObj.other.content,
      },
    });
  } catch (error) {
    result = null;
  }

  return result;
};

/**
 * Method to check if input form are valid, correct type and not empty if required
 * @param {string} input value of input filed
 * @param {boolean} required information if filed is required
 * @param {type} type type of data, it can be :"string" || "number"
 * @param {number} minValue the minimum value of the numeric input value
 * @param {number} maxValue the maximum value of the numeric input value
 * @returns {object} information if data is valid and errorMesaage for bad data
 */
export const inputsValidator = (input, required, type, minValue, maxValue) => {
  let result = {
    isValid: true,
    errorMessage: '',
  };

  if (input !== undefined && input !== null && input.length !== 0) {
    switch (type) {
      case 'integer':
        let reg = new RegExp('^-?[0-9]+$');
        if (!reg.test(input)) {
          result.isValid = false;
          result.errorMessage = 'Please use integers only';
        } else if (typeof minValue === 'number' && input < minValue) {
          result.isValid = false;
          result.errorMessage = 'The value should be higher than ' + (minValue - 1);
        } else if (typeof maxValue === 'number' && input > maxValue) {
          result.isValid = false;
          result.errorMessage = 'The value should be lower than ' + (maxValue + 1);
        }
        break;
      case 'number': {
        const float = Number.parseFloat(input);
        if (Number.isNaN(float)) {
          result.isValid = false;
          result.errorMessage = 'Please use numbers only';
        } else if (typeof minValue === 'number' && input < minValue) {
          result.isValid = false;
          result.errorMessage = 'The value should be higher than ' + (minValue - 1);
        } else if (typeof maxValue === 'number' && input > maxValue) {
          result.isValid = false;
          result.errorMessage = 'The value should be lower than ' + (maxValue + 1);
        }
        break;
      }
      case 'percents':
        input
          .trim()
          .split(',')
          .forEach(item => {
            item = item.trim();
            item = Number.parseFloat(item);

            if (Number.isNaN(item) || item <= 0 || item > 100) {
              result.isValid = false;
              result.errorMessage =
                'It should be a set of numbers between 0 and 100, separated by ","';
            }
          });
        break;
      case 'timecodes':
        input
          .trim()
          .split(',')
          .forEach(item => {
            let duration = -1,
              tmp;

            item = item.trim();

            if (
              item !== '' &&
              !item.match(/[^0-9|.|:]|\.(:)|:(\.)|:(:)|^\D|\D$/) &&
              (!item.includes('.') || item.lastIndexOf('.') > item.lastIndexOf(':')) &&
              (!item.match(/\./g) || (item.match(/\./g) && item.match(/\./g).length < 2)) &&
              (!item.match(/:/g) || (item.match(/:/g) && item.match(/:/g).length < 3))
            ) {
              duration = Number.parseInt(item, 10);
              tmp = item.match(/(\d+):(\d+):(\d+)/);

              if (tmp) {
                duration =
                  Number.parseInt(tmp[1], 10) * 3600 +
                  Number.parseInt(tmp[2], 10) * 60 +
                  Number.parseInt(tmp[3], 10);
              } else {
                tmp = item.match(/(\d+):(\d+)/);

                if (tmp) {
                  duration = Number.parseInt(tmp[1], 10) * 60 + Number.parseInt(tmp[2], 10);
                }
              }

              tmp = item.match(/(\.\d+)/);

              if (tmp) {
                duration += Number.parseFloat(tmp[1], 10);
              }
            }

            if (duration < 0) {
              result.isValid = false;
              result.errorMessage = 'Please use the patterns described in the documentation';
            }
          });
        break;
      default:
        break;
    }
  } else if (required) {
    result.isValid = false;
    result.errorMessage = 'This field is required';
  }
  return result;
};

/**
 * Method to setup jwplayer
 * @param {string} containerId id of player container
 * @param {object} setupObject setup of jwplayer
 * @param {function} registerPlugin A function used for integration samples
 */
const setupJWPlayer = (containerId, setupObject, registerPlugin) => {
  if (globalThis.jwplayer && registerPlugin !== null && typeof registerPlugin === 'function') {
    setupObject.events = {};
    setupObject.events.onReady = function () {
      if (registerPlugin !== null && typeof registerPlugin === 'function') {
        registerPlugin();
      }
    };
  }

  if (globalThis.jwplayer) {
    globalThis.jwplayer(containerId).setup(setupObject);
  }
};

/**
 * Method to embed jwplayer version 5 or 6
 * @param {string} containerId id of player container
 * @param {boolean} autoplay setup of autoplay for player
 * @param {boolean} muted setup of mute for player
 * @param {function} registerPlugin A function used for integration samples
 * @param {boolean} isVersion6 informatin if player is version 6 or 5
 */
const embedJWPlayer5or6 = (containerId, autoplay, muted, registerPlugin, isVersion6) => {
  let setupObject = {};
  setupObject.autostart = autoplay;
  setupObject.mute = muted;
  setupObject.repeat = false;
  setupObject.width = '99%';

  if (isVersion6) {
    // load script from corect localization in other case it's trying to load from current do
    $.getScript('//ced-ns.sascdn.com/video/players/jwplayer/6.12/jwplayer.html5.js', () => {
      setupObject.primary = 'html5';
      setupObject.height = '100%';
      setupObject.playlist = playlist;
      setupJWPlayer(containerId, setupObject, registerPlugin);
    });
  } else {
    setupObject.modes = [
      {
        type: 'html5',
      },
    ];
    setupObject['controlbar.position'] = 'bottom';
    setupObject['controlbar.idlehide'] = false;
    setupObject['logo.hide'] = true;
    setupObject.dock = true;
    setupObject.height = samplesMinPlayerHeight;
    setupObject.levels = playlistJW5.levels;
    setupObject.image = playlistJW5.image;
    setupJWPlayer(containerId, setupObject, registerPlugin);
  }
};

/**
 * Method to embed jwplayer version > 6
 * @param {string} containerId id of player container
 * @param {boolean} autoplay setup of autoplay for player
 * @param {boolean} muted setup of mute for player
 * @param {function} registerPlugin A function used for integration samples
 */
const embedJWPlayer = (containerId, autoplay, muted, registerPlugin) => {
  let setupObject = {};
  setupObject.autostart = autoplay;
  setupObject.height = '100%';
  setupObject.mute = muted;
  setupObject.playlist = playlist;
  setupObject.repeat = false;
  setupObject.width = '100%';

  if (globalThis.jwplayer) {
    globalThis.jwplayer(containerId).setup(setupObject);
  }

  if (globalThis.jwplayer && registerPlugin !== null && typeof registerPlugin === 'function') {
    globalThis.jwplayer(containerId).on('ready', () => {
      registerPlugin();
    });
  }
};

/**
 * Method to embed flowplayer version 5
 * @param {string} containerId id of player container
 * @param {string} playerName name of player
 * @param {object} playerList data with playlist
 * @param {string} version version of player
 * @param {string} script url to the required script
 * @param {function} registerPlugin A function used for integration samples
 */

const embedFlowplayer5 = (containerId, playerName, playerList, version, script, registerPlugin) => {
  const onFlowplayerReady = () => {
    const currentPlaylist = globalThis.playlist; // renamed to avoid shadowing
    const container = document.getElementById(containerId);

    if (!currentPlaylist || !container) return;

    const videoSources = currentPlaylist[0].sources
      .map(item => `<source key="${item.type}" type="video/${item.type}" src="${item.file}"/>`)
      .join('');

    container.innerHTML = `
      <div class="flowplayer" id="${containerId}" style="width: 100%;">
        <video poster="${currentPlaylist[0].image}">${videoSources}</video>
      </div>
    `;

    if (typeof registerPlugin === 'function') {
      globalThis.flowplayer(api => registerPlugin(api));
    }
  };

  const loadPlayerLib = () => {
    const libUrl = playerList[playerName].libLinks[version][0];
    $.getScript(libUrl, () => globalThis.setTimeout(onFlowplayerReady, 0));
  };

  $.getScript(script, loadPlayerLib);
};

/**
 * Method loads players
 * @param {String} playerName The players name
 * @param {Number} version The version's index in player.version array
 * @param {String} containerId The id of the player's container tag
 * @param {Boolean} autoplay The value of the autoplay attribute
 * @param {Boolean} isASample Determinate if method is used for integation samples
 * @param {Function} registerPlugin A function used for integration samples
 */
export const loadPlayer = (
  playerName,
  version,
  containerId,
  autoplay,
  isASample = false,
  registerPlugin = null
) => {
  const query = decodeURLParams(globalThis.location.search);
  const playerList = isASample ? samplePlayers : players;
  const muted = query.testMode || isMobile ? true : false;
  switch (playerName) {
    case 'brightcove':
      if (version === '6') {
        $('#' + containerId)[0].innerHTML =
          ' <iframe src="//players.brightcove.net/2436837446001/BkoDY6aUW_default/index.html?videoId=4941088215001"' +
          'allowfullscreen ' +
          'webkitallowfullscreen ' +
          'mozallowfullscreen ' +
          'width="100%" height="' +
          samplesMinPlayerHeight +
          '"></iframe>';
      } else {
        $('#' + containerId)[0].innerHTML =
          '<video id=' +
          containerId +
          ' data-video-id="2503191864001" data-account="2436837446001" data-player="V1TJ1ahCg" ' +
          'data-embed="default" class="video-js" controls height="' +
          samplesMinPlayerHeight +
          '" width="100%"></video>';
        $.getScript(playerList[playerName].libLinks[version][0]);
      }
      if (registerPlugin !== null && typeof registerPlugin === 'function') {
        registerPlugin();
      }
      break;
    case 'flowplayer':
      $('<link/>', {
        rel: 'stylesheet',
        type: 'text/css',
        href: playerList[playerName].libLinks[version][1],
      }).appendTo('head');
      if (version === '5.4.6') {
        embedFlowplayer5(
          containerId,
          playerName,
          playerList,
          version,
          playerList[playerName].libLinks[version][2],
          registerPlugin
        );
      } else {
        $.getScript(playerList[playerName].libLinks[version][0], () => {
          globalThis
            .flowplayer($('#' + containerId)[0], {
              clip: {
                sources: playlist[0].sources.map(item => {
                  return {
                    type: 'video/' + item.type,
                    src: item.file,
                  };
                }),
              },
              poster: playlist[0].image,
              muted: muted,
              autoplay: autoplay,
            })
            .one('ready', (e, api) => {
              if (registerPlugin !== null && typeof registerPlugin === 'function') {
                registerPlugin(api);
                api.trigger('ready', [api, api.video]);
              }
            });

          if (query.testMode || isMobile) {
            // for flowplayer 6 set muted in cofiguration desn't work properly, it's mute the player
            // but if we read property video.muted it's return false - so our plugin play with sound
            if ($('#' + containerId + ' video')[0]) {
              $('#' + containerId + ' video')[0].muted = true;
            }
          }
        });
      }
      break;
    case 'jwplayer':
      $.getScript(playerList[playerName].libLinks[version], () => {
        if (version === '5.10' || version === '6.12') {
          embedJWPlayer5or6(
            containerId,
            autoplay,
            muted,
            registerPlugin,
            version === playerList[playerName].versions[1]
          );
        } else {
          embedJWPlayer(containerId, autoplay, muted, registerPlugin);
        }
      });
      break;
    case 'ooyala':
      $.getScript(playerList[playerName].libLinks[version], () => {
        if (globalThis.OO !== undefined) {
          globalThis.OO.ready(() => {
            globalThis.OO.Player.create(containerId, '9hd3RyYzqmLffkuIcEzSqDDcqIMTIQVf', {
              height: samplesMinPlayerHeight,
              onCreate: () => {
                if (registerPlugin !== null && typeof registerPlugin === 'function') {
                  registerPlugin();
                }
              },
            });
          });
        }
      });
      break;
    default:
      globalThis.setTimeout(() => {
        $('#' + containerId)[0].innerHTML =
          '<video id="mainPlayer" style="width:100%; height:100%; object-fit: fill" ' +
          (autoplay ? 'autoplay' : '') +
          ' controls ' +
          (query.testMode || isMobile ? 'muted' : '') +
          ' playsinline poster="' +
          playlist[0].image +
          '">' +
          playlist[0].sources
            .map(
              item =>
                '<source key="' +
                item.type +
                '" type="video/' +
                item.type +
                '" src="' +
                item.file +
                '"/>'
            )
            .join('') +
          '</video>';

        if (registerPlugin !== null && typeof registerPlugin === 'function') {
          registerPlugin();
        }
      }, 0);
      break;
  }
};

/**
 * Method remove the localStorage variable's value
 * @param {String} name the name of cookie variable
 * @param {String} value the value of the cookie variable to set
 */
export const removeLocalStorage = name => {
  if (globalThis.localStorage.getItem(name) !== undefined) {
    globalThis.localStorage.removeItem(name);
  }
};

/**
 * Method to check if player is defined in samplePlayers object
 * @param {string} name the name of player
 * @param {string} version the version of player
 * @returns {boolean} true if player id defined, false if not
 */
export const checkIfSampleExist = (name, version = null) => {
  let exist = samplePlayers[name] !== undefined;
  if (exist && version !== null) {
    exist = samplePlayers[name].versions.find(item => {
      return item === version;
    });
  }
  return exist;
};
/**
 * Method to get default version of player
 * @param {string} name the name of player
 * @returns {string} default version fo player
 */
export const getDefaultPlayerVersion = name => {
  return samplePlayers[name].default;
};

/**
 * Method to select controller lib version. The selected script is embedded in head section
 * By passing in url query string you can decide if the version of controller would be from local or from production
 * local='your controller domain' you should specify a domain from which we can get lib http://currentdomain/video-project/video/libs/js/
 * verbose=true
 * Example : ?local=http://localhost/video-project/video&verbose=true
 * @returns {null}
 */
export const scriptSelector = () => {
  const query = decodeURLParams(globalThis.location.search);
  let controllerFile = '//r.sascdn.com/video/controller.js?nwid=73';

  if (globalThis.location.host.indexOf('dev') > 0) {
    controllerFile = '//apps-dev.sascdn.com/diff/video/latest/libs/js/controller.js';
  }

  if (query.local) {
    controllerFile =
      query.local + '/libs/js/' + (query.verbose ? 'verbose/controller.max.js' : 'controller.js');
  }
  $('<script/>', {
    type: 'text/javascript',
    src: controllerFile,
  }).appendTo('head');
};

/**
 * Method sets the localStorage variable's value
 * @param {String} name the name of cookie variable
 * @param {String} value the value of the cookie variable to set
 */
export const setLocalStorage = (name, value) => {
  globalThis.localStorage.setItem(name, value);
};

/**
 * Method starts the player
 * @param {String} playerName the player's name from the current state
 * @param {String} containerId the container's id from the current state
 */
export const startPlayer = (playerName, containerId) => {
  switch (playerName) {
    case 'flowplayer':
      setTimeout(() => {
        globalThis.flowplayer().play();
      }, 0);
      break;
    case 'jwplayer':
      globalThis.jwplayer(containerId).load(playlist);
      globalThis.jwplayer(containerId).play();
      break;
    default:
      if (isMobile) {
        let eventName = isIPhone ? 'touchstart' : 'touchend',
          eventTarget = isIPad ? '#container' : '#mainPlayer',
          handler = function () {
            $(eventTarget)[0].removeEventListener(eventName, handler);
            $('#mainPlayer')[0].play();
          };

        $(eventTarget)[0].addEventListener(eventName, handler);
        let uiEvent = document.createEvent('UIEvent');
        uiEvent.initEvent(eventName, true, true);
        $(eventTarget)[0].dispatchEvent(uiEvent);
      } else {
        $('#mainPlayer')[0].play();
      }
      break;
  }
};

/**
 * Method stops the player
 * @param {String} playerName the player's name from the current state
 * @param {String} containerId the container's id from the current state
 */
export const stopPlayer = (playerName, containerId) => {
  const muteState = decodeURLParams(globalThis.location.search).testMode || isMobile;
  switch (playerName) {
    case 'flowplayer':
      if (globalThis.flowplayer) {
        globalThis.flowplayer().stop();
        if ($('#' + containerId + ' video')[0]) {
          $('#' + containerId + ' video')[0].muted = muteState;
        }
      }
      break;
    case 'jwplayer':
      if (globalThis.jwplayer) {
        globalThis.jwplayer(containerId).stop();
        globalThis.jwplayer(containerId).setMute(muteState);
      }
      break;
    default:
      $('#mainPlayer')[0].pause();
      $('#mainPlayer')[0].currentTime = 0;
      $('#mainPlayer')[0].muted = muteState;
      break;
  }
};

/**
 * Method to update the size of the Section Panel Component
 */
export const updateSize = () => {
  let tabcontent = $('#section>div:first-child'),
    tabcontentMaxHeight =
      $(globalThis).height() -
      $('.btnPanel').outerHeight() -
      Number.parseInt($('.btnPanel').css('margin-bottom'), 10) / 2 -
      tabcontent.position().top;
  // 900px is a value from css @media (orientation: landscape) and (min-width: 900px)
  if (
    ((tabcontent.css('max-height') !== '100%' &&
      tabcontentMaxHeight > Number.parseInt(tabcontent.css('max-height'), 10)) ||
      tabcontentMaxHeight < tabcontent.outerHeight()) &&
    $(globalThis).width() > 900
  ) {
    tabcontent.css('max-height', tabcontentMaxHeight);
  } else if ($(globalThis).width() < 900) {
    tabcontent.css('max-height', '100%');
    tabcontent.css('overflow', '');
  }
};

/**
 * Method to check that at least one of the fields are valid
 * @param {event} event
 * @param {string} type type of field, 'string' or 'number'
 * @param {boolean} required information if parameter is required
 * @param {function} updateStateFn the method to update the state
 * @param {number} minValue the minimum value of the numeric input value
 * @param {number} maxValue the maximum value of the numeric input value
 */
export const validateField = (event, type, required, updateStateFn, minValue, maxValue) => {
  let result = inputsValidator(event.target.value, required, type, minValue, maxValue),
    errorElement = $(event.target.parentNode).find('.validationError');

  errorElement.hide();
  $(event.target).removeClass('textFiledError');
  if (!result.isValid) {
    errorElement.text(result.errorMessage);
    errorElement.show();
    $(event.target).addClass('textFiledError');
  }

  if (updateStateFn && (!event.triggered || !result.isValid)) {
    updateStateFn();
  }
};
/**
 * Method to parse to Float, but before call js parseFloat it replace "," by "."
 * @param {string} value input field value
 * @return {number|NaN|''}
 */
export const parseToFloat = value => {
  return value.length ? Number.parseFloat(value.replace(',', '.')) : '';
};
/**
 * Merge two objects, if some attribute from obj1 do not exist in obj2 then takes value from obj1
 * @param {Object} obj1 default object, full defined with all existing keys
 * @param {Object} obj2 input data from user
 * @returns Object new object, if obj2 has all existing keys then result = obj2, if no, missing keys/values will be added from obj1
 */
const mergeObjects = (obj1, obj2) => {
  let result = {};
  Object.keys(obj1).forEach(key => {
    if (typeof obj1[key] === 'object' && obj1[key] !== null && obj1[key].length === undefined) {
      result[key] = obj2[key] === undefined ? obj1[key] : mergeObjects(obj1[key], obj2[key]);
    } else if (obj2[key] === undefined) {
      result[key] = obj1[key];
    } else {
      result[key] = obj2[key];
    }
  });
  return result;
};

const mergeAdRules = (obj1, obj2) => {
  let result = [];
  Object.keys(obj1).forEach(key => {
    if (obj1[key] !== undefined) {
      let temp = mergeObjects(obj2, obj1[key]);
      result.push(temp);
    }
  });
  return result;
};

/**
 * Function to setup Google Tag Manager on the page
 * @param {string} gtmId Google Tag Manager ID
 * @return {void}
 */
export const setupGoogleTagManager = gtmId => {
  const script = document.createElement('script');
  const code = `
        (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '& l=' + l : '';
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${gtmId}');
    `;
  script.textContent = code;
  document.head.insertBefore(script, document.head.firstChild);
};

/**
 * Function to check if provided string is an URL
 * @param {string} str
 * @returns {boolean}
 */
export const isUrl = str => {
  const urlRegex =
    /^\s*(?:(?:https?:\/\/)?(?:[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,})|(?:https?:\/\/)?\blocalhost\b)(?::[0-9]+)?\/?\S*\s*$/;

  return urlRegex.test(str);
};

/**
 * Function to display maxLength characters from a string
 * @param {string} str
 * @param {number} maxLength
 * @returns {string}
 */
export const truncateString = (str, maxLength) => {
  if (str.length <= maxLength) {
    return str;
  } else {
    return str.slice(0, maxLength) + '...';
  }
};

/**
 * Function to create player configuration object from state
 * @param {state} state
 * @returns
 */
export const createExportConfigurationObject = state => {
  const optionalParameters = {};
  if (state.other.content.sas_contentid || state.other.content.sas_videoContentDuration) {
    optionalParameters.contentData = {};
    if (state.other.content.sas_contentid) {
      optionalParameters.contentData.sas_contentid = state.other.content.sas_contentid;
    }
    if (state.other.content.sas_videoContentDuration) {
      optionalParameters.contentData.sas_videoContentDuration =
        state.other.content.sas_videoContentDuration;
    }
  }
  if (state.other.user.uid) {
    optionalParameters.user = {
      uid: state.other.user.uid,
    };
  }

  const exportData = {
    id: 'mainPlayer /*PASS YOUR MAIN PLAYER TAG ID HERE*/',
    contentPlayer: {
      api: null,
    },
    adRules: state.adRules.data,
    adData: {
      sas_format_linears: state.adData.sas_format_linears,
      sas_format_overlays: state.adData.sas_format_overlays,
      sas_networkid: state.adData.sas_networkid,
      sas_pageid: state.adData.sas_pageid,
      sas_siteid: state.adData.sas_siteid,
      sas_target: state.adData.sas_target,
    },
    configuration: {
      labels: state.configuration.labels,
      overlays: state.configuration.overlays,
      banner: state.configuration.banner,
      player: state.configuration.player,
      publisher: state.configuration.publisher,
      rtb: state.configuration.rtb,
      vpaid: state.configuration.vpaid,
    },
    ...optionalParameters,
  };

  return exportData;
};
